import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { ActivityItemData, ContactActivityData, ContactDetailData } from '@/types/api';

export type ContactActivityKind = 'call' | 'email' | 'note' | 'meeting' | 'sms' | 'custom';

export const CONTACT_ACTIVITY_KINDS: ContactActivityKind[] = [
  'call',
  'email',
  'note',
  'meeting',
  'sms',
  'custom',
];

export type ContactActivityInput = {
  kind: ContactActivityKind;
  title: string;
  body?: string | null;
  happened_at?: string | null;
};

const DETAIL_KEY = (id: number) => ['contact', id];
const FEED_KEY = (id: number) => ['contact-activity', id];

export function useContact(contactId: number | undefined) {
  return useQuery({
    queryKey: contactId == null ? ['contact', null] : DETAIL_KEY(contactId),
    enabled: contactId != null,
    queryFn: async (): Promise<ContactDetailData> => {
      const { data } = await api.get<ContactDetailData>(`/v1/contacts/${contactId}`);
      return data;
    },
  });
}

/** Calls, texts, logged notes, and web inquiries for one person, newest first. */
export function useContactActivity(contactId: number | undefined) {
  return useQuery({
    queryKey: contactId == null ? ['contact-activity', null] : FEED_KEY(contactId),
    enabled: contactId != null,
    queryFn: async (): Promise<ActivityItemData[]> => {
      const { data } = await api.get<ActivityItemData[]>(`/v1/contacts/${contactId}/activity`);
      return data;
    },
  });
}

/**
 * Log a touch against a person. Pass `leadId` when the entry is being written
 * from a lead screen so it records which pipeline conversation it came from;
 * either way it lands on the contact's feed.
 */
export function useCreateContactActivity(contactId: number, leadId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ContactActivityInput): Promise<ContactActivityData> => {
      await primeCsrf();
      const url =
        leadId != null
          ? `/v1/leads/${leadId}/activities`
          : `/v1/contacts/${contactId}/activities`;
      const { data } = await api.post<ContactActivityData>(url, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEED_KEY(contactId) });
      if (leadId != null) qc.invalidateQueries({ queryKey: ['leads', leadId] });
    },
  });
}

export function useDeleteContactActivity(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (activityId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/contacts/${contactId}/activities/${activityId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: FEED_KEY(contactId) }),
  });
}
